import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'

/**
 * One primary action with the rest of the row's actions folded in beside it.
 *
 * The board header used to carry every action as its own control, which meant a row of
 * equally-weighted buttons where only one of them — the one that leaves the building — is
 * ever the thing you came to do. The others are housekeeping, and housekeeping belongs one
 * click down rather than permanently on screen.
 *
 * Items hide rather than disable in here, which is the opposite of the rule the header
 * itself follows. That rule exists because the header is right-anchored, so a control that
 * comes and goes shifts everything beside it sideways; inside a menu there is nothing to
 * shift, and a list of greyed-out entries that are not applicable to the current view is
 * just noise on the way to the one that is.
 */
export default function SplitButton({ children, onClick, disabled, title, items = [] }) {
  const [open, setOpen] = useState(false)
  // Which sub-list is expanded, if any. One at a time — these are short lists of
  // alternatives, and the menu reads better with a single thing unfolded.
  const [openSub, setOpenSub] = useState(null)
  // The MORE button, not the whole control: the panel hangs off the half it belongs to, and
  // a click on the primary button beside it should close the menu like any other outside
  // click rather than counting as a click on its own anchor.
  const moreRef = useRef(null)
  const closeTimer = useRef(null)

  // Opens on hover, as asked — but a pointer crossing the gap between the button and the
  // panel below it briefly leaves both, and closing on that flicker makes the menu feel
  // broken. A short grace period is the whole fix.
  const openNow = () => {
    clearTimeout(closeTimer.current)
    setOpen(true)
  }
  const closeSoon = () => {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => { setOpen(false); setOpenSub(null) }, 180)
  }

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  // Click-away, Escape, placement and the portal all come from Popover — the one place
  // every anchored panel in the app gets them. This menu used to draw its own panel from
  // `.filter-panel` alone, which stopped carrying any chrome when that moved onto
  // `.popover`: no background, no shadow, and no `position: fixed`, so it landed in the
  // header's normal flow and shoved the board sideways.
  const close = useCallback(() => { setOpen(false); setOpenSub(null) }, [])

  // Only the items this view actually offers, with separators worked out afterwards — a
  // divider is a relationship between two things that are present, so it cannot be decided
  // until we know which ones those are.
  const shown = items.filter((it) => it && it.show !== false)

  return (
    <div className="split-btn" onMouseLeave={closeSoon}>
      <button className="btn primary split-btn-main" onClick={onClick} disabled={disabled} title={title}>
        {children}
      </button>
      <button
        type="button"
        ref={moreRef}
        className={`btn primary split-btn-more ${open ? 'open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        onMouseEnter={openNow}
        // Deliberately not onFocus. Closing the panel hands focus back to this button (see
        // Popover), so opening on focus meant the menu shut and immediately reopened itself
        // — move the pointer away after touching any row and it would not go. Keyboard
        // access is the click handler: Enter and Space fire it, which is how a menu button
        // is meant to work anyway, and tabbing past this one no longer unfurls a menu.
        onClick={() => (open ? setOpen(false) : openNow())}
      >
        <Icon name="more" size={16} strokeWidth={2.2} />
      </button>

      <Popover
        open={open}
        anchorRef={moreRef}
        close={close}
        className="filter-panel split-menu"
        role="menu"
        label="More actions"
        // Right-aligned because the button sits at the right edge of the header: opening
        // leftward is what keeps the panel on screen.
        align="right"
        // A sub-list unfolding changes the panel's height, so where it sits is worked out
        // again — otherwise an expanded menu near the bottom of the window runs off it.
        placeKey={openSub}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
      >
        {shown.length === 0 && <div className="ctx-label">Nothing to do here</div>}
        {/* A row that opens a sub-list waits to be CLICKED. Opening on hover meant the
            menu grew and reflowed while the pointer was only passing over that row on its
            way to the item below it. */}
        {shown.map((it, i) => (
          <div key={it.key} className={it.children ? 'split-sub-host' : undefined}>
            {i > 0 && <div className="ctx-sep" />}
            <button
              type="button"
              className="ctx-item"
              disabled={it.disabled}
              title={it.title}
              aria-haspopup={it.children ? 'menu' : undefined}
              aria-expanded={it.children ? openSub === it.key : undefined}
              onClick={
                it.children
                  ? () => setOpenSub((k) => (k === it.key ? null : it.key))
                  : () => { setOpen(false); setOpenSub(null); it.onClick() }
              }
            >
              {it.icon && <Icon name={it.icon} size={14} />}
              <span>{it.label}</span>
              {it.children && (
                <Icon
                  name={openSub === it.key ? 'down' : 'right'}
                  size={13}
                  className="split-sub-arrow"
                />
              )}
            </button>

            {/* Unfolds in place instead of flying out sideways. The panel it lives in is
                a scroll container, and a scroll container clips on both axes — so a
                submenu positioned outside it was drawn nowhere at all. Inline, there is
                no gap to cross, nothing to clip it, and no second hover target to lose. */}
            {it.children && openSub === it.key && (
              <div className="ctx-sub" role="group" aria-label={it.label}>
                {it.children.map((sub) => (
                  <button
                    key={sub.key}
                    type="button"
                    className={`ctx-item ctx-sub-item ${sub.checked ? 'on' : ''}`}
                    onClick={() => { setOpen(false); setOpenSub(null); sub.onClick() }}
                  >
                    <span className="ctx-item-check">
                      {sub.checked && <Icon name="check" size={13} strokeWidth={2.4} />}
                    </span>
                    <span>{sub.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </Popover>
    </div>
  )
}
